import type { CommodityGroup, CgHierarchyHeader } from '@whiz-cart/node-shared/models/commodityGroup/commodityGroup';
import type { CommodityGroupFilter } from '@whiz-cart/node-shared/models/commodityGroup/commodityGroupFilter';
import type { DeepPartial } from '@whiz-cart/node-shared/types/deepPartial';
import { createCache, get, type AnyPath, type Cache, type ValidPath, type Value } from 'cross-state';
import { defaultsDeep } from 'lodash';
import { createContext, useContext, type ReactNode } from 'react';

export interface ErrorHandler<TId = unknown> {
    showError(error: unknown): TId[] | void;
    closeError?(id: TId): void;
}

export interface UiSharedConfig {
    storeGuid: string | undefined;
    lineItem: {
        displayDiscounts: boolean;
    };
    currency: string;
    errorHook: (error: unknown) => void;
    errorHandlerHook?: () => ErrorHandler;
    commodityGroupService: {
        commodityGroups: (filter?: CommodityGroupFilter) => Cache<CommodityGroup[], []>;
        commodityGroupsLookup: (filter?: CommodityGroupFilter) => Cache<Map<string, CommodityGroup>, []>;
        cgHierarchyHeaders: () => Cache<CgHierarchyHeader[], []>;
    };
    inventoryManagementSystemService: {
        map: Cache<Map<number, string>, []>;
    };
}

const defaultUiSharedConfig: UiSharedConfig = {
    storeGuid: undefined,
    lineItem: {
        displayDiscounts: false,
    },
    currency: 'EUR',
    errorHook: (error: unknown) => {
        if (error !== undefined) {
            console.error('An error occurred:', error);
        }
    },
    commodityGroupService: {
        commodityGroups: createCache(async () => [] as CommodityGroup[]),
        commodityGroupsLookup: createCache(async () => new Map<string, CommodityGroup>()),
        cgHierarchyHeaders: createCache(async () => [] as CgHierarchyHeader[]),
    },
    inventoryManagementSystemService: {
        map: createCache(async () => new Map()),
    },
};

export const UiSharedContext = createContext<UiSharedConfig>(defaultUiSharedConfig);

export function UiSharedProvider({ children, ...config }: { children?: ReactNode } & DeepPartial<UiSharedConfig>) {
    return <UiSharedContext.Provider value={defaultsDeep({}, config, defaultUiSharedConfig)}>{children}</UiSharedContext.Provider>;
}

export function useUiSharedConfig(): UiSharedConfig;
export function useUiSharedConfig<const K extends AnyPath>(key: ValidPath<UiSharedConfig, K>): Value<UiSharedConfig, K>;
export function useUiSharedConfig(key?: any): any {
    const config = useContext(UiSharedContext);
    return get(config as any, key ?? []);
}
